import { useEffect, useRef as useReactRef, type RefObject } from 'react';

/**
 * Hook that detects clicks outside of the specified elements.
 * Commonly used for closing dropdowns, modals, and popovers when clicking outside.
 *
 * @param refs - Array of refs to elements that should NOT trigger the callback
 * @param callback - Function to call when click occurs outside all refs
 * @param enabled - Whether the hook is active (default: true)
 *
 * @example
 * const dropdownRef = useRef<HTMLDivElement>(null);
 * const triggerRef = useRef<HTMLButtonElement>(null);
 *
 * useClickOutside([dropdownRef, triggerRef], () => {
 *   setIsOpen(false);
 * }, isOpen);
 */
export function useClickOutside(
  refs: Array<RefObject<HTMLElement | null>>,
  callback: () => void,
  enabled: boolean = true
): void {
  const refsRef = useReactRef(refs);
  const callbackRef = useReactRef(callback);

  useEffect(() => {
    refsRef.current = refs;
    callbackRef.current = callback;
  });

  useEffect(() => {
    if (!enabled) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;

      // Check if click is inside any of the refs
      const isInsideAnyRef = refsRef.current.some(
        (ref) => ref.current?.contains(target)
      );

      // Call callback only if click is outside all refs
      if (!isInsideAnyRef) {
        callbackRef.current();
      }
    };

    // Use capture phase to catch events before they bubble
    document.addEventListener('mousedown', handleClickOutside, true);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
    };
  }, [enabled, refsRef, callbackRef]);
}

/**
 * Simplified version for single element.
 * Use useClickOutside for multiple elements.
 */
export function useClickOutsideSingle(
  ref: RefObject<HTMLElement | null>,
  callback: () => void,
  enabled: boolean = true
): void {
  useClickOutside([ref], callback, enabled);
}
